import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, LogOut, CheckCircle, AlertTriangle } from 'lucide-react';
import '../Admin/AdminDashboard.css';

interface SubscriptionFormData {
  user_id: string;
  email: string;
  plan_type: string;
  payment_method: string;
}

interface ApiResponse {
  message: string;
  expires_at?: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SubscriptionFormData>({
    user_id: '',
    email: '',
    plan_type: 'MONTHLY',
    payment_method: 'BANK'
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<ApiResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Get admin email from localStorage
  const adminEmail = localStorage.getItem('adminEmail') || 'admin@homecookt.com';

  const handleInputChange = (field: keyof SubscriptionFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    // Clear success/error messages when form is modified
    if (successMessage) setSuccessMessage(null);
    if (errorMessage) setErrorMessage('');
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    // Validate user_id
    if (!formData.user_id.trim()) {
      newErrors.user_id = 'User ID is required';
    } else if (isNaN(Number(formData.user_id)) || Number(formData.user_id) <= 0) {
      newErrors.user_id = 'User ID must be a positive number';
    }

    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Validate plan_type
    if (!['WEEKLY', 'MONTHLY', 'YEARLY'].includes(formData.plan_type)) {
      newErrors.plan_type = 'Please select a valid plan type';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage(null);
    setErrorMessage('');

    try {
      // Use hardcoded admin token as specified
      const adminToken = '1234';
      
      console.log('Making API call with admin token:', adminToken);
      console.log('Request payload:', {
        user_id: Number(formData.user_id),
        email: formData.email,
        plan_type: formData.plan_type,
        payment_method: formData.payment_method
      });

      const response = await fetch('https://api.homecookt.com/api/v1/manual/activate-subscription', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'x-admin-token': adminToken, // Using hardcoded admin token 1234
        },
        body: JSON.stringify({
          user_id: Number(formData.user_id),
          email: formData.email,
          plan_type: formData.plan_type,
          payment_method: formData.payment_method
        }),
      });

      console.log('API Response status:', response.status);

      if (response.ok) {
        const data: ApiResponse = await response.json();
        console.log('API Response data:', data);
        
        setSuccessMessage(data);
        
        // Reset form after successful submission
        setFormData({
          user_id: '',
          email: '',
          plan_type: 'MONTHLY',
          payment_method: 'BANK'
        });
        
        // Scroll to success message
        setTimeout(() => {
          const successElement = document.querySelector('.homecookt-success-message');
          if (successElement) {
            successElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.log('API Error data:', errorData);
        
        let errorMsg = 'Failed to activate subscription';
        
        switch (response.status) {
          case 403:
            errorMsg = 'Access denied. Invalid admin token.';
            break;
          case 400:
            errorMsg = errorData.message || 'Invalid request. Please check the user details or plan type.';
            break;
          case 404: 
            errorMsg = 'User not found. Please verify the User ID and email address.';
            break;
          case 500:
            errorMsg = 'Server error. Please try again later.';
            break;
          default:
            errorMsg = errorData.message || `Request failed with status ${response.status}`;
        }
        
        setErrorMessage(errorMsg);
      }
    } catch (error) {
      console.error('Network error:', error);
      
      // Check if it's a CORS error
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        setErrorMessage(
          'CORS Error: The admin API endpoint is not accessible from this domain. ' +
          'This needs to be fixed on the backend by adding CORS headers for localhost:3001. ' +
          'Please contact the backend developer to add CORS support for the manual subscription endpoint.'
        );
      } else {
        setErrorMessage('Network error. Please check your connection and try again.');
      }
      
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    // Clear admin authentication data
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminPermissions');
    
    // Navigate back to admin login
    navigate('/admin');
  };

  const formatExpiryDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="homecookt-admin-dashboard-page">
      {/* Header */}
      <header className="homecookt-admin-header">
        <div className="homecookt-admin-header-content">
          <div className="homecookt-admin-logo">
            <Shield size={24} />
            <span>HomeCookt Admin</span>
          </div>
          
          <div className="homecookt-admin-user-info">
            <div className="homecookt-admin-user-details">
              <span className="homecookt-admin-user-email">{adminEmail}</span>
              <span className="homecookt-admin-user-role">Administrator</span>
            </div>
            <button onClick={handleLogout} className="homecookt-admin-logout-btn">
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="homecookt-admin-main">
        <h1 className="homecookt-admin-title">Admin Dashboard</h1>
        <p className="homecookt-admin-subtitle">
          Manage subscriptions and monitor platform activity
        </p>

        {/* Subscription Activation Form */}
        <div className="homecookt-subscription-form-container">
          <h2 className="homecookt-form-title">Manual Subscription Activation</h2>
          <p className="homecookt-form-description">
            Activate a subscription plan for a user. All fields marked with * are required.
          </p>

          <form onSubmit={handleSubmit} className="homecookt-subscription-form">
            {/* User ID */}
            <div className="homecookt-form-group">
              <label htmlFor="user_id" className="homecookt-form-label">
                User ID <span className="homecookt-required-asterisk">*</span>
              </label>
              <input
                id="user_id"
                type="number"
                value={formData.user_id}
                onChange={(e) => handleInputChange('user_id', e.target.value)}
                className={`homecookt-form-input ${errors.user_id ? 'error' : ''}`}
                placeholder="Enter user ID (e.g., 123)"
                min="1"
                required
              />
              {errors.user_id && (
                <div className="homecookt-error-message">
                  <AlertTriangle size={16} />
                  {errors.user_id}
                </div>
              )}
            </div>

            {/* Email */}
            <div className="homecookt-form-group">
              <label htmlFor="email" className="homecookt-form-label">
                Email Address <span className="homecookt-required-asterisk">*</span>
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`homecookt-form-input ${errors.email ? 'error' : ''}`}
                placeholder="user@example.com"
                required
              />
              {errors.email && (
                <div className="homecookt-error-message">
                  <AlertTriangle size={16} />
                  {errors.email}
                </div>
              )}
            </div>

            {/* Plan Type with Pricing */}
            <div className="homecookt-form-group">
              <label htmlFor="plan_type" className="homecookt-form-label">
                Plan Type <span className="homecookt-required-asterisk">*</span>
              </label>
              <select
                id="plan_type"
                value={formData.plan_type}
                onChange={(e) => handleInputChange('plan_type', e.target.value)}
                className={`homecookt-form-select ${errors.plan_type ? 'error' : ''}`}
                required
              >
                <option value="WEEKLY">Weekly Plan - £3.99/week</option>
                <option value="MONTHLY">Monthly Plan - £4.99/month</option>
                <option value="YEARLY">Yearly Plan - £49.99/year</option>
              </select>
              {errors.plan_type && (
                <div className="homecookt-error-message">
                  <AlertTriangle size={16} />
                  {errors.plan_type}
                </div>
              )}
            </div>

            {/* Payment Method - Read Only */}
            <div className="homecookt-form-group">
              <label htmlFor="payment_method" className="homecookt-form-label">
                Payment Method
              </label>
              <input
                id="payment_method"
                type="text"
                value="Bank Transfer"
                className="homecookt-form-input"
                readOnly
                style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
              />
            </div>

            {/* Submit Button */}
            <div className="homecookt-form-group full-width">
              <button
                type="submit"
                disabled={isSubmitting}
                className="homecookt-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <div className="homecookt-submit-spinner"></div>
                    <span>Activating...</span>
                  </>
                ) : (
                  <>
                    <Shield size={20} />
                    <span>Activate Subscription</span>
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Success Message */}
          {successMessage && (
            <div className="homecookt-success-message">
              <div className="homecookt-success-icon">
                <CheckCircle size={24} />
              </div>
              <div className="homecookt-success-content">
                <h3 className="homecookt-success-title">Subscription Activated Successfully!</h3>
                <p className="homecookt-success-details">{successMessage.message}</p>
                {successMessage.expires_at && (
                  <p className="homecookt-success-expiry">
                    Expires: {formatExpiryDate(successMessage.expires_at)}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Error Message */}
          {errorMessage && (
            <div className="homecookt-error-alert">
              <div className="homecookt-error-icon">
                <AlertTriangle size={24} />
              </div>
              <div className="homecookt-error-content">
                <h3 className="homecookt-error-title">Activation Failed</h3>
                <p className="homecookt-error-details">{errorMessage}</p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;